import {    FormatHelper,
            ConfigService,
            UserMessageTypeEnum,
            BotMessageTypeEnum,
            BotMessageTypeFromIndex,
            ErrorMessageTypeEnum,
            IAttachment,
            MessageEntity,
            DialogEntity,
            SessionEntity,
            AttachmentFactory,
            NotificationEntity }                    from '../../../';

/**
* The message factory is responsible of business logic related to 
* messages and conversation dialogs instantiation rules.
*/
export class MessageFactory { 

    /**
    * Returns a new dialog entity initialized with its firts message.
    * 
    * @param firstMsg - The first message associated to this dialog.
    */
    public static buildNewDialogEntity(firstMsg?: MessageEntity): DialogEntity {
        const msgList: MessageEntity[] = [];
        if (firstMsg) {
            msgList.push(firstMsg);
        }
        return new DialogEntity(Date.now(), msgList);
    }

    /**
    * Returns a message entity.
    * 
    * @param messageContent - The message content as it has been sent or received from server, no formatting or processing is done
    * @param sessionInfo - The user session information state at the moment of the message has been created
    */
    public static buildIntroductionMessage(messageContent: string, sessionInfo: SessionEntity): MessageEntity {
        return new MessageEntity(   -1,
                                    BotMessageTypeEnum.INTRODUCTION,
                                    sessionInfo.botName,
                                    sessionInfo.conversationId,
                                    messageContent,
                                    FormatHelper.formatDateForBotServer(new Date()));
    }

    /**
    * Returns a message entity.
    * 
    * @param messageContent - The message content as it has been sent or received from server, no formatting or processing is done
    * @param sessionInfo - The user session information state at the moment of the message has been created
    */
    public static buildNotificationMessage(messageContent: string, sessionInfo: SessionEntity): MessageEntity {
        return new MessageEntity(   sessionInfo.nextMsgWatermark,
                                    BotMessageTypeEnum.NOTIFICATION,
                                    sessionInfo.botName,
                                    sessionInfo.conversationId,
                                    messageContent,
                                    FormatHelper.formatDateForBotServer(new Date()));
    }

    public static buildNotificationEntity(rawNotif : any): NotificationEntity{
        return new NotificationEntity(  rawNotif.watermark,
                                        rawNotif.from,
                                        rawNotif.conversationId,
                                        rawNotif.text,
                                        rawNotif.time,
                                        rawNotif.id,
                                        false,
                                        true);
    }

    /**
    * Returns a message entity.
    * 
    * @param messageContent - The message content as it has been sent or received from server, no formatting or processing is done
    * @param sessionInfo - The user session information state at the moment of the message has been created
    */
    public static buildErrorMessage(messageContent: string, sessionInfo: SessionEntity, typeError: ErrorMessageTypeEnum): MessageEntity {
        return new MessageEntity(   sessionInfo.nextMsgWatermark,
                                    typeError,
                                    sessionInfo.botName,
                                    sessionInfo.conversationId,
                                    messageContent,
                                    FormatHelper.formatDateForBotServer(new Date()));
    }

    /**
    * Returns a user message entity.
    * 
    * @param sessionInfo - The user session information state at the moment of the message has been created
    * @param messageContent - The message content as it has been sent or received from server, no formatting or processing is done
    * @param attachments - The message attachments list
    */
    public static buildCommandMessage(sessionInfo: SessionEntity, messageContent: string, attachments?: Map<number, IAttachment[]>): MessageEntity {
        return new MessageEntity(   sessionInfo.nextMsgWatermark,
                                    UserMessageTypeEnum.WIDGET_COMMAND,
                                    ConfigService.getBotUserOrigin(),
                                    sessionInfo.conversationId,
                                    messageContent,
                                    FormatHelper.formatDateForBotServer(new Date()),
                                    false,
                                    0,
                                    attachments);
    }

    /**
    * Returns a user message entity.
    * 
    * @param sessionInfo - The user session information state at the moment of the message has been created
    * @param msgType - The message type
    * @param messageContent - The message content as it has been sent or received from server, no formatting or processing is done
    * @param attachments - The message attachments list
    */
    public static buildMessageToSend(sessionInfo: SessionEntity, msgType: UserMessageTypeEnum, messageContent: string, attachments?: Map<number, IAttachment[]>): MessageEntity {
        return new MessageEntity(   sessionInfo.nextMsgWatermark,
                                    msgType,
                                    ConfigService.getBotUserOrigin(),
                                    sessionInfo.conversationId,
                                    messageContent,
                                    FormatHelper.formatDateForBotServer(new Date()),
                                    false,
                                    0,
                                    attachments);
    }

    /**
    * Returns a message entity list according to the bot answer.
    * 
    * @param botAnswerActivities - The activities list returned by into the BotServer answer object.
    */
    public static buildMessageEntitiesFromBotAnswer(botAnswerActivities: any[]): MessageEntity[] {
        const answerMessages: MessageEntity[] = [];
        
        botAnswerActivities.forEach((activity: any) => {
            // parse only the bot messages
            if (activity.from === ConfigService.synapseBotId) {
                 
                const msgType: BotMessageTypeEnum | undefined = activity.messageType ? BotMessageTypeFromIndex[activity.messageType] 
                                                                                     : BotMessageTypeEnum.BASIC_ANSWER;
                if (msgType !== BotMessageTypeEnum.USER_QUESTION) {
                    const botResponses: MessageEntity[] = [];
                    let additionalAnswerIdx: number = 0;
                    const msg: MessageEntity =  MessageFactory.buildMessageWithAttachments(   activity.watermark,
                                                                                            msgType,
                                                                                            activity.from,
                                                                                            activity.conversationId,
                                                                                            activity.text,
                                                                                            activity.time,
                                                                                            (activity.otherMessages && activity.otherMessages.length > 0),
                                                                                            additionalAnswerIdx++,
                                                                                            activity.attachments);
                    botResponses.push(msg);

                    if (msgType != BotMessageTypeEnum.INTRODUCTION && activity.otherMessages && activity.otherMessages.length > 0) {
                        activity.otherMessages.forEach((otherAnswer: any) => {
                            const otherAnswerType: BotMessageTypeEnum | undefined = activity.messageType ? BotMessageTypeFromIndex[activity.messageType] 
                                                                                                         : BotMessageTypeEnum.BASIC_ANSWER;
                                                                                                         
                            const otherMsg: MessageEntity = MessageFactory.buildMessageWithAttachments(   activity.watermark,
                                                                                                        otherAnswerType,
                                                                                                        activity.from,
                                                                                                        activity.conversationId,
                                                                                                        otherAnswer.text,
                                                                                                        activity.time,
                                                                                                        true,
                                                                                                        additionalAnswerIdx++,
                                                                                                        otherAnswer.attachments);
                            botResponses.push(otherMsg);
                        });
                    }
                    msg.displayableResponses = botResponses;
                    answerMessages.push(msg);
                }
            }
        });
        return answerMessages;
    }

    /**
     *  Returns a message entity list according to the bot reaction after a user's satisfaction feedback.
     * 
     * @param messageContent - The bot message to display after a user's satisfaction feedback.
     * @param sessionInfo - The associated conversation session entity.
     * @returns The MessageEntity instance to display
     */
    public static buildMessageEntityFromBotSatisfactionFeedback(messageContent: string, sessionInfo: SessionEntity, watermark: number): MessageEntity {
        const msg: MessageEntity =  new MessageEntity(    watermark,
                                                        BotMessageTypeEnum.BASIC_ANSWER,
                                                        sessionInfo.botName,
                                                        sessionInfo.conversationId,
                                                        messageContent,
                                                        FormatHelper.formatDateForBotServer(new Date()),
                                                        false,
                                                        -1,
                                                        new Map<number, IAttachment[]>());
        msg.displayableResponses = [];
        msg.displayableResponses.push(msg);
        return msg;
    }   
   
   /**
     * Return a new msg entity with attachments
     * 
     * @param watermark - The associated watermark
     * @param messageType  - The message type it could be the UserMessageTypeEnum or BotMessageTypeEnum value
     * @param from - The sender identifier
     * @param conversationId  - The conversation identifier
     * @param text  - The message content
     * @param time - The message timestamp
     * @param isOtherMessages - Indicates whetner or not this message has other possible responses
     * @param counter - Indice to handle otherResponses id
     * @param activityAttachments - The associated attachments
     * @returns a messageEntity
     */
    private static buildMessageWithAttachments( watermark: number, 
                                                messageType: any, 
                                                from: string, 
                                                conversationId: string, 
                                                text: string, 
                                                time: string, 
                                                isOtherMessages:boolean, 
                                                counter: number, 
                                                activityAttachments: any[]): MessageEntity {

        const msg: MessageEntity = new MessageEntity( watermark,
                                                    messageType,
                                                    from,
                                                    conversationId,
                                                    text,
                                                    time,
                                                    isOtherMessages,
                                                    counter);

        const attachments: Map<number, IAttachment[]> | undefined = AttachmentFactory.buildMessageAttachmentsFromBotAnswer( msg.msgId, 
                                                                                                                            activityAttachments,
                                                                                                                            watermark);
        msg.attachments = attachments ? attachments : new Map<number, IAttachment[]>();
        return msg;
    }
}
